import React, { useState, useEffect } from 'react';
import FileBrowser from './FileBrowser';
import { buildFileTree } from '../utils/fileTree';
import { API_BASE_URL } from '../config/constants';

// Files and directories to ignore when indexing
const indexIgnoreFolders = ['__pycache__', '.venv', 'node_modules', '.git', 'codeflow', 'bonzai'];
const indexIgnoreFiles = ['package.json', 'package-lock.json', '.gitignore', '.DS_Store'];

/**
 * Check if a file path should be ignored based on indexIgnore patterns
 * @param {string} filePath - The file path to check
 * @returns {boolean} - True if the path should be ignored
 */
function shouldIgnorePath(filePath) {
  const parts = filePath.split('/');
  const fileName = parts[parts.length - 1];

  // Check if any folder in the path should be ignored (skip root folder - index 0)
  const foldersToCheck = parts.slice(1);
  if (foldersToCheck.some(part => indexIgnoreFolders.includes(part))) {
    return true;
  }

  // Check if the file itself should be ignored
  if (indexIgnoreFiles.includes(fileName)) {
    return true;
  }

  return false;
}

function FileList({ onFileClick, onFolderClick, onTreeUpdate, refreshTrigger }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const fetchFileList = async (isInitial = false) => {
    try {
      // Only show loading state on initial load
      if (isInitial) {
        setLoading(true);
      }
      
      const response = await fetch(`${API_BASE_URL}/list`, {
        method: 'GET',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }

      // Try to parse as JSON first, fall back to text
      let fileArray = [];
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        fileArray = data.files || data || [];
      } else {
        // Try parsing as JSON text
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          fileArray = data.files || data || [];
        } catch (e) {
          // If not JSON, treat as newline-separated file paths
          fileArray = text.trim().split('\n').filter(line => line.trim());
        }
      }
      
      // Filter out ignored files and directories
      const filteredFileArray = fileArray.filter(filePath => !shouldIgnorePath(filePath));
      
      setFiles(filteredFileArray);
      setError(null);
      
      // Build tree and notify parent
      if (onTreeUpdate && filteredFileArray.length > 0) {
        const tree = buildFileTree(filteredFileArray);
        onTreeUpdate(tree);
      }
    } catch (err) {
      console.error('Error fetching file list:', err);
      setError(err.message);
      setFiles([]);
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchFileList(true); // Initial load with loading state
    // Refresh every 5 seconds without showing loading state
    const interval = setInterval(() => fetchFileList(false), 5000);
    return () => clearInterval(interval);
  }, []);

  // Watch for refresh trigger
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchFileList(false);
    }
  }, [refreshTrigger]);

  const handleFileClick = (file) => {
    if (onFileClick) {
      onFileClick(file);
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: '#fafafa',
      borderRight: '1px solid #e5e5e5',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e5e5',
        background: '#fafafa',
        fontSize: '13px',
        fontWeight: '600',
        color: '#1e1e1e',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0
      }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', opacity: 0.6 }}>
              <path d="M3.196 12.87l-.825.483a.75.75 0 000 1.294l7.25 4.25a.75.75 0 00.758 0l7.25-4.25a.75.75 0 000-1.294l-.825-.484-5.666 3.322a2.25 2.25 0 01-2.276 0L3.196 12.87z" />
              <path d="M3.196 8.87l-.825.483a.75.75 0 000 1.294l7.25 4.25a.75.75 0 00.758 0l7.25-4.25a.75.75 0 000-1.294l-.825-.484-5.666 3.322a2.25 2.25 0 01-2.276 0L3.196 8.87z" />
              <path d="M10.38 1.103a.75.75 0 00-.76 0l-7.25 4.25a.75.75 0 000 1.294l7.25 4.25a.75.75 0 00.76 0l7.25-4.25a.75.75 0 000-1.294l-7.25-4.25z" />
            </svg>
            <span>Files</span>
          </div>
          <div style={{
            fontSize: '11px',
            color: '#6b7280',
            fontWeight: '400',
            paddingLeft: '24px'
          }}>
            all code stays on your machine
          </div>
        </div>
      </div>
      {(
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '8px 0'
      }}>
        {loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontSize: '13px' }}>
            Loading files...
          </div>
        )}
        {error && (
          <div style={{
            padding: '15px',
            margin: '8px',
            background: '#f8d7da',
            border: '1px solid #dc3545',
            borderRadius: '4px',
            color: '#721c24',
            fontSize: '12px'
          }}>
            ❌ Error: {error}
          </div>
        )}
        {!loading && !error && (
          <FileBrowser 
            files={files} 
            onFileClick={handleFileClick}
            onFolderClick={onFolderClick}
          />
        )}
      </div>
      )}
    </div>
  );
}

export default FileList;

